import { domToBlob } from "modern-screenshot";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSupportChatContext } from "../contexts/support-chat-context";
import type { SupportConversation, SupportMessage } from "../types";
import { useApi } from "./use-api";
import { useApiMutation } from "./use-api-mutation";
import { useApiQuery } from "./use-api-query";
import { useWebSocket } from "./use-websocket";

export function useSupportChat() {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { callApi } = useApi<{ image_url: string }>();

  // Get pending conversation from context (set by notification click)
  const { pendingConversationId, clearPendingConversation, setViewingConversationId } =
    useSupportChatContext();

  // Get active conversation
  const {
    data: activeConversation,
    isLoading: isLoadingConversation,
    refetch: refetchConversation,
  } = useApiQuery<SupportConversation | null>("/support/active-conversation", {
    refetchInterval: false, // Don't poll for this
  });

  // Get conversation history (all conversations including closed)
  const {
    data: conversationHistory,
    isLoading: isLoadingHistory,
    refetch: refetchHistory,
  } = useApiQuery<SupportConversation[]>("/support/conversations/history", {
    refetchInterval: false,
  });

  // Set conversation ID when we have an active one (only on initial load)
  useEffect(() => {
    if (activeConversation?.id && !conversationId) {
      setConversationId(activeConversation.id);
    }
  }, [activeConversation, conversationId]);

  // Sync viewing conversation ID to context (for notification suppression)
  useEffect(() => {
    setViewingConversationId(conversationId);
  }, [conversationId, setViewingConversationId]);

  // Handle pending conversation from notification click
  useEffect(() => {
    if (pendingConversationId) {
      // Refetch history to ensure we have the latest conversations
      refetchHistory().then(() => {
        // After refetch, the conversationHistory will update and trigger the switch
      });
    }
  }, [pendingConversationId, refetchHistory]);

  // Switch to pending conversation once history is loaded
  useEffect(() => {
    if (pendingConversationId && conversationHistory?.length) {
      const conversation = conversationHistory.find((c) => c.id === pendingConversationId);
      if (conversation) {
        setConversationId(conversation.id);
        setShowHistory(false);
      }
      clearPendingConversation();
    }
  }, [pendingConversationId, conversationHistory, clearPendingConversation]);

  // Get messages for conversation
  const {
    data: fetchedMessages,
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
  } = useApiQuery<SupportMessage[]>(
    conversationId ? `/support/conversations/${conversationId}/messages` : "",
    {
      enabled: !!conversationId,
      refetchInterval: false, // WebSocket handles real-time updates
    },
  );

  // Update messages when fetched
  useEffect(() => {
    if (fetchedMessages) {
      setMessages(fetchedMessages);
    }
  }, [fetchedMessages]);

  // Create conversation mutation
  const { mutateAsync: createConversation, isPending: isCreating } = useApiMutation<
    { subject?: string; url?: string },
    SupportConversation
  >("POST", "/support/conversations");

  // Send message mutation - URL changes based on conversation
  const { mutateAsync: sendMessageMutation, isPending: isSending } = useApiMutation<
    { content: string; image_url?: string; _url?: string },
    SupportMessage
  >("POST", "/support/conversations");

  // Close conversation mutation
  const { mutateAsync: closeConversationMutation, isPending: isClosing } = useApiMutation<
    { _url?: string },
    SupportConversation
  >("POST", `/support/conversations/${conversationId}/close`);

  // WebSocket for real-time updates
  useWebSocket<unknown, SupportMessage>(
    conversationId ? `support/ws/${conversationId}` : "",
    (msg) => {
      // Add new message from WebSocket
      setMessages((prev) => {
        // Avoid duplicates
        if (prev.some((m) => m.id === msg.id)) {
          return prev;
        }
        return [...prev, msg];
      });
    },
    !!conversationId, // Only connect when we have a conversation
  );

  // Scroll to bottom when messages change
  useEffect(() => {
    // Use setTimeout to ensure DOM is updated before scrolling
    const timeoutId = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
    return () => clearTimeout(timeoutId);
  }, []);

  // Start a new conversation
  const startConversation = useCallback(
    async (subject?: string) => {
      const conversation = await createConversation({
        subject,
        url: window.location.href,
      });
      setConversationId(conversation.id);
      setMessages([]);
      setShowHistory(false);
      return conversation;
    },
    [createConversation],
  );

  // Upload an image and get the S3 path
  const uploadImage = useCallback(
    async (file: File, currentConversationId: string): Promise<string> => {
      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const result = await callApi(
          `/support/conversations/${currentConversationId}/upload-image`,
          {
            method: "POST",
            body: formData,
          },
        );
        return result.image_url;
      } finally {
        setIsUploading(false);
      }
    },
    [callApi],
  );

  // Send a message (optionally with an image)
  const sendMessage = useCallback(
    async (content: string, imageUrl?: string) => {
      let currentConversationId = conversationId;
      const isNewConversation = !currentConversationId;

      if (!currentConversationId) {
        // Start a new conversation first
        const conversation = await startConversation();
        currentConversationId = conversation.id;
      }

      const message = await sendMessageMutation({
        content,
        image_url: imageUrl,
        _url: `/support/conversations/${currentConversationId}/messages`,
      });
      setMessages((prev) => [...prev, message]);

      // Refetch history so the new conversation appears in the list
      if (isNewConversation) {
        await refetchHistory();
      }

      return message;
    },
    [conversationId, sendMessageMutation, startConversation, refetchHistory],
  );

  // Send a message with an image file
  const sendMessageWithImage = useCallback(
    async (content: string, file: File) => {
      let currentConversationId = conversationId;

      if (!currentConversationId) {
        // Start a new conversation first
        const conversation = await startConversation();
        currentConversationId = conversation.id;
      }

      // Upload the image first
      const imageUrl = await uploadImage(file, currentConversationId);

      // Then send the message with the image URL (sendMessage handles history refetch)
      return sendMessage(content, imageUrl);
    },
    [conversationId, startConversation, uploadImage, sendMessage],
  );

  // Capture a screenshot and send it
  const captureAndSendScreenshot = useCallback(async () => {
    setIsCapturingScreenshot(true);
    try {
      let currentConversationId = conversationId;
      const isNewConversation = !currentConversationId;

      if (!currentConversationId) {
        // Start a new conversation first
        const conversation = await startConversation();
        currentConversationId = conversation.id;
      }

      // Capture the page using modern-screenshot (better CSS support)
      const blob = await domToBlob(document.body, {
        filter: (el) => {
          if (!(el instanceof Element)) return true;
          return (
            !el.classList.contains("support-chat-container") &&
            el.getAttribute("data-screenshot-ignore") !== "true"
          );
        },
        scale: Math.min(window.devicePixelRatio, 2),
      });

      if (!blob) {
        throw new Error("Failed to capture screenshot");
      }

      const file = new File([blob], "screenshot.png", { type: "image/png" });

      // Upload and send with current URL as context
      const imageUrl = await uploadImage(file, currentConversationId);
      const message = await sendMessage(window.location.href, imageUrl);

      // Refetch history if new conversation
      if (isNewConversation) {
        await refetchHistory();
      }

      return message;
    } catch (error) {
      console.error("Screenshot capture failed:", error);
      throw error;
    } finally {
      setIsCapturingScreenshot(false);
    }
  }, [conversationId, startConversation, uploadImage, sendMessage, refetchHistory]);

  // Close the current conversation
  const closeConversation = useCallback(async () => {
    if (!conversationId) return;

    await closeConversationMutation({
      _url: `/support/conversations/${conversationId}/close`,
    });

    // Clear current conversation and refresh
    setConversationId(null);
    setMessages([]);
    await refetchConversation();
    await refetchHistory();
  }, [conversationId, closeConversationMutation, refetchConversation, refetchHistory]);

  // Switch to a different conversation
  const switchConversation = useCallback((conversation: SupportConversation | null) => {
    if (conversation) {
      setConversationId(conversation.id);
      setShowHistory(false);
    } else {
      setConversationId(null);
      setMessages([]);
    }
  }, []);

  // Get the current conversation object
  const currentConversation = conversationHistory?.find((c) => c.id === conversationId);

  return {
    conversationId,
    currentConversation,
    messages,
    conversationHistory: conversationHistory || [],
    showHistory,
    setShowHistory,
    isLoading: isLoadingConversation || isLoadingMessages,
    isLoadingHistory,
    isSending: isSending || isCreating || isUploading,
    isUploading,
    isCapturingScreenshot,
    isClosing,
    sendMessage,
    sendMessageWithImage,
    captureAndSendScreenshot,
    startConversation,
    closeConversation,
    switchConversation,
    refetchMessages,
    refetchConversation,
    refetchHistory,
    messagesEndRef,
  };
}
